/**
 * A table component used as the primary editing interface for asset tags.
 * Values are managed as groups via their summary rows in the table.
 */
import React from 'react';
import { PiTagFill } from 'react-icons/pi';
import type { AssetType } from '@kentik/ui-shared/labels/lib';
import { inject, observer } from 'mobx-react';
import { IReactComponent } from 'mobx-react/dist/types/IReactComponent';
import { EmptyState, Flex, Icon, Spinner } from 'core/components';
import { AssetTagsStore } from 'app/stores/assetTags/$assetTags';
import AssetTagInventoryCollection from 'app/stores/assetTags/AssetTagInventoryCollection';
import AdminTable from 'app/components/admin/AdminTable';
import InventoryColumns from './InventoryColumns';
import groupSummaryLookup from './groupSummary/groupSummaryLookup';
import AddTagSelector from './AddTagSelector';
import EditCounts from './EditCounts';

interface Props {
  $assetTags: AssetTagsStore;
  assetType: AssetType;
  collection: AssetTagInventoryCollection;
  onSearch: (search: string) => void;
  singleMode?: boolean;
}

function InventoryTable(props: Props) {
  const { $assetTags, assetType, collection, onSearch, singleMode } = props;

  if (collection.loading) {
    // waiting to render the table until after load ensures groups are all collapsed and columns are sized correctly on first render
    return (
      <Flex justifyContent="center" flex={1}>
        <Spinner />
      </Flex>
    );
  }

  return (
    <InventoryColumns $assetTags={$assetTags} assetType={assetType} singleMode={singleMode}>
      {(columns: unknown[]) => (
        <AdminTable
          collection={collection}
          columns={columns}
          tableContainerProps={{ bg: 'appBackground' }}
          onSearch={onSearch}
          groupSummaryLookup={groupSummaryLookup}
          emptyState={
            <EmptyState icon={<Icon icon={PiTagFill} iconSize={48} />} title={`No ${collection.assetLabel} Tags`} />
          }
          tableHeaderControlsPosition="afterSearch"
          tableHeaderControls={$assetTags.canUpdate ? <AddTagSelector collection={collection} /> : null}
          footer={<EditCounts collection={collection} />}
          isCollapsed
        />
      )}
    </InventoryColumns>
  );
}

export default inject('$assetTags')(observer(InventoryTable as IReactComponent));
